import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  UserCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import authClient from '../client/authClient';
import { AuthUser } from '../types/auth.types';
import { VerificationLinkError } from '../utils/authUtils';
import { clearRobbieActivity } from '../utils/robbieActivity';

interface AuthContextType {
  currentUser: User | null;
  backendUser: AuthUser | null;
  /**
   * Replace the cached backend user after a write that returns the updated
   * document (e.g. an onboarding dismissal) — one source of truth for the
   * user, refreshed from the response rather than re-fetched.
   */
  applyBackendUser: (user: AuthUser) => void;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Signs in with a Firebase custom token (the one the backend returns when
   * it confirms a verification link) and syncs the backend user exactly as
   * `login` does. Rejects when either half fails; a failed backend login
   * also signs Firebase out.
   */
  loginWithCustomToken: (customToken: string) => Promise<void>;
  /**
   * Creates the Firebase account, asks the backend to email the verification
   * link and leaves the app signed out — no backend account exists until the
   * address is verified, so the caller shows the "check your inbox" state
   * instead of proceeding. Rejects with `VerificationLinkError` when the
   * account was created but the link could not be requested.
   */
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  /** Re-sends the verification link for an account that is not verified yet. */
  resendVerificationEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<{ user: User }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [backendUser, setBackendUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // TODO: CHECK AND REMOVE LOGIC OF USER ID IN LOCAL STORAGE AND RENAME TO LOGIN
  const loginToBackend = async (user: User) => {
    try {
      const firebaseUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        providerData: user.providerData
      };

      const response = await authClient.login(firebaseUser);

      if (response && response.user) {
        // The backend login has just written this user's custom claims; a
        // refreshed token carries them, so the next buyer-service request is
        // accepted first time instead of being refused and retried.
        await user.getIdToken(true).catch(() => undefined);
        setBackendUser(response.user);
        // Store MongoDB user ID in localStorage
        if (response.user.userId) {
          localStorage.setItem('user', response.user.userId);
        }
      }
    } catch (error) {
      console.error('Failed to login to backend:', error);
      // If backend login fails, we must log out from Firebase to prevent access to protected routes
      await signOut(auth);
      setCurrentUser(null);
      setBackendUser(null);
      throw error; // Re-throw to let the caller know
    }
  };

  /**
   * One sign-in path for every credential: Firebase first, then the backend.
   * The auth listener still sets `currentUser` but skips its own backend sync
   * while this runs, so the user is upserted once.
   */
  const signInAndSync = async (signIn: () => Promise<UserCredential>) => {
    try {
      loginInProgress.current = true;
      const userCredential = await signIn();
      // Force a fresh ID token so a link the person just clicked is reflected
      // in the `email_verified` claim the backend checks (a cached token still
      // says unverified). The request interceptor reuses this refreshed token.
      await userCredential.user.getIdToken(true);
      await loginToBackend(userCredential.user);
    } finally {
      loginInProgress.current = false;
    }
  };

  const login = (email: string, password: string) =>
    signInAndSync(() => signInWithEmailAndPassword(auth, email, password));

  const loginWithCustomToken = (customToken: string) =>
    signInAndSync(() => signInWithCustomToken(auth, customToken));

  /**
   * The backend sends the verification email (our sender, design and sandbox
   * override). The token is refreshed so it carries the display name just
   * set. Signs out whatever happens: the backend provisions nothing until the
   * address is verified, and PrivateRoute only checks `currentUser`, so the
   * app must not hold a Firebase session here. The person signs in again
   * after clicking the link.
   */
  const sendVerificationLinkAndSignOut = async (user: User) => {
    try {
      const idToken = await user.getIdToken(true);
      await authClient.requestEmailVerification(idToken);
    } catch {
      throw new VerificationLinkError();
    } finally {
      await signOut(auth);
    }
  };

  const signup = async (email: string, password: string, displayName?: string) => {
    try {
      loginInProgress.current = true;
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      if (displayName && userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: displayName,
        });
        await userCredential.user.reload();
      }

      await sendVerificationLinkAndSignOut(userCredential.user);
    } finally {
      loginInProgress.current = false;
    }
  };

  /**
   * Sends another verification link. The session is signed out while the
   * address is unverified, so this re-authenticates with the credentials the
   * person just entered, requests the link, and signs out again.
   */
  const resendVerificationEmail = async (email: string, password: string) => {
    try {
      loginInProgress.current = true;
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await sendVerificationLinkAndSignOut(userCredential.user);
    } finally {
      loginInProgress.current = false;
    }
  };

  const logout = async () => {
    localStorage.removeItem('user');
    clearRobbieActivity();
    setBackendUser(null);
    await signOut(auth);
  };

  const loginWithGoogle = async () => {
    try {
      loginInProgress.current = true;
      const userCredential = await signInWithPopup(auth, googleProvider);
      await loginToBackend(userCredential.user);
      return { user: userCredential.user };
    } finally {
      loginInProgress.current = false;
    }
  };

  const loginInProgress = React.useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false); // Always stop loading on auth change

      // If manual login is in progress, let it handle backend sync to avoid duplicates
      if (loginInProgress.current) return;

      if (user) {
        // Prevent re-triggering if backendUser is already set (optimization)
        if (backendUser && backendUser.email === user.email) return;

        // Auto-sync for page reloads / token refreshes
        loginInProgress.current = true;
        try {
          // 1. Try to get existing session details first (lighter check)
          try {
            const meResponse = await authClient.getMe();
            if (meResponse && meResponse.user) {
              setBackendUser(meResponse.user);
              if (meResponse.user.userId) {
                localStorage.setItem('user', meResponse.user.userId);
              }
              return; // Successfully synced
            }
          } catch (e) {
            console.log('Session check failed, attempting full login:', e);
          }

          // 2. If getMe failed, perform full login (upsert user)
          await loginToBackend(user);
        } finally {
          loginInProgress.current = false;
        }
      } else {
        // Also reached when the session ends without the button (expiry, another tab).
        clearRobbieActivity();
        setBackendUser(null);
      }
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    backendUser,
    applyBackendUser: setBackendUser,
    loading,
    login,
    loginWithCustomToken,
    signup,
    resendVerificationEmail,
    logout,
    loginWithGoogle,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
